import Router from '@/main/router';

import i18n from '@/plugins/i18n';

import { isNil, isEmpty } from './values';
import {
  EAvailabilityRedirect,
  EEligibilityAction,
  EOrderSuccessPages,
  EErrorExhibition,
  EErrorType,
  EErrorSeverity,
  EErrorScope,
  EPaymentMethodTypes
} from './enums';

import { ICheckoutCartOfferAvailability, ICheckoutCartOfferEligibility, ICheckout } from '@/interfaces/Checkout';
import { IntervalType } from '@/interfaces/IntervalType';

import TraceableError from '@/classes/TraceableError';

import { checkoutEarlyRenew } from '@/api/resources/checkout';

import { redirectAvailabilityErrorOr404 } from '@/helpers/redirects';
import { getErrorByCodeAndType, addError, getDefaultErrorByType } from '@/helpers/errors';
import { sendInteractEvent } from './events';
import { EInteractEventTrackerAction } from '@/app/features/InteractEventTracker/enums/EInteractEventTracker';

export const intervalBuild = (intervalType: string, intervalCount: number, isRenew: boolean = true, onlyInterval: boolean = false, ignoreCount: boolean = false): string => {
  if (intervalType !== IntervalType.lifelong) {
    let isPlural = intervalType && intervalType.substr(intervalType.length - 1, intervalType.length) === 's';

    if (isPlural && intervalCount <= 1 || ignoreCount && isPlural) {
      intervalType = intervalType.substr(0, intervalType.length - 1);
    }

    if (onlyInterval) {
      return String(i18n.t(`App.calendar.${intervalType}`));
    }

    const template = String(`
      ${isRenew ? 'por mais' : 'por'}
      ${intervalCount}
      ${i18n.t(`App.calendar.${intervalType}`)}
    `);

    return template;
  }

  return String(`${isRenew ? i18n.t('App.for') : ''} ${i18n.t(`App.calendar.${intervalType}`)}`);
};

const typeOfAvailabilityRedirect = (availability: ICheckoutCartOfferAvailability): EAvailabilityRedirect | string => {
  return (!isNil(availability.redirect_slug) && !isEmpty(availability.redirect_slug))
    ? EAvailabilityRedirect.redirect_slug
    : (!isNil(availability.redirect_url) && !isEmpty(availability.redirect_url))
      ? EAvailabilityRedirect.redirect_url
      : '';
};

export const applyAvailability = (availability: ICheckoutCartOfferAvailability, error_code: number | string | null): boolean => {
  const response = {
    isAvailable: availability.available,
    willRedirect: false,
    redirectCallback: () => { }
  } as any;

  if (!availability.available) {
    const availabilityRedirectType = typeOfAvailabilityRedirect(availability);

    if (availabilityRedirectType === EAvailabilityRedirect.redirect_slug) {
      response.willRedirect = true;
      response.redirectCallback = (callback: Function) => {
        Router.replace({
          name: 'checkout',
          params: { slug: availability.redirect_slug },
          query: Router.currentRoute.query,
        } as any);
        callback();
      };
    }

    if (availabilityRedirectType === EAvailabilityRedirect.redirect_url) {
      response.willRedirect = true;
      response.redirectCallback = () => {
        const urlSearchParams = new URLSearchParams(location.search);
        const newUrl = new URL(availability.redirect_url!);

        for (let [key, value] of urlSearchParams.entries()) {
          if (!newUrl.searchParams.has(key)) {
            newUrl.searchParams.append(key, value);
          }
        }

        window.location.replace(newUrl.href);
      };
    }

    if (isEmpty(availabilityRedirectType)) {
      response.willRedirect = false;
      response.redirectCallback = () => { };
    }
  }

  return response;
};

export interface IAppliedEligibility {
  isEligible: boolean;
  isRenewal: boolean;
  checkout: ICheckout | null;
  redirectCallback: Function;
}

export const applyEligibility = async (eligibility: ICheckoutCartOfferEligibility | null, error_code: number | string | null): Promise<any> => {
  let appliedEligibility: IAppliedEligibility = {
    isEligible: false,
    isRenewal: false,
    checkout: null,
    redirectCallback: () => { },
  };

  const isRenewRoute = Router.currentRoute.name === 'renew';

  if (eligibility) {
    appliedEligibility.isEligible = eligibility.eligible;
    appliedEligibility.isRenewal = [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal].includes(eligibility.eligible_action);

    if (appliedEligibility.isEligible) {
      if (!isRenewRoute) {
        appliedEligibility.redirectCallback = () => {
          return Router.replace({
            name: appliedEligibility.isRenewal ? 'renew' : 'payment',
            params: {
              ...Router.currentRoute.params,
              ...appliedEligibility.isRenewal && {
                slug: eligibility.early_renewal_contract
              },
            },
            query: Router.currentRoute.query,
          });
        };
      }

      if (appliedEligibility.isRenewal && !isRenewRoute) {
        try {
          const earlyRenewalCheckout = await checkoutEarlyRenew(eligibility.early_renewal_contract, { payment_method: EPaymentMethodTypes.creditCard });

          appliedEligibility.checkout = earlyRenewalCheckout.data;

          let errorCode = appliedEligibility.checkout!.cart.offer!.eligibility!.error_code;

          if (errorCode) {
            errorCode = errorCode < 8000
              ? errorCode
              : 'expiredLink';

            const traceableError = getErrorByCodeAndType(errorCode, [EErrorType.eligibility]);

            if (traceableError) {
              throw new TraceableError({
                ...traceableError,
                type: EErrorType.eligibility,
                scope: EErrorScope.global,
                trigger: 'applyEligibility',
                exhibition: errorCode != 'expiredLink'
                  ? EErrorExhibition.modal
                  : EErrorExhibition.alert,
              });
            }
          }
        } catch (error) {
          if (error.response) {
            const traceableError = getDefaultErrorByType(EErrorType.availability);

            sendInteractEvent({
              action: EInteractEventTrackerAction.CHK_RENEWAL_REDIRECT_FAILED,
              attributes: {
                return_code: error.response.status,
              },
            });

            throw new TraceableError({
              ...traceableError,
              type: EErrorType.availability,
              code: error.response ? error.response.status : 'default',
              scope: EErrorScope.global,
              trigger: 'applyEligibility',
              severity: EErrorSeverity.critical,
              exhibition: EErrorExhibition.modal,
            });
          }

          throw error;
        }
      }
    } else if (error_code) {
      const error = getErrorByCodeAndType(error_code, [EErrorType.eligibility]);

      if (error) {
        throw new TraceableError({
          ...error,
          type: EErrorType.eligibility,
          scope: EErrorScope.global,
          trigger: 'applyEligibility',
          exhibition: EErrorExhibition.modal,
        });
      }
    }
  }

  return appliedEligibility;
};

export const redirectToSuccessPage = (status: string, action: EEligibilityAction, params?: any): any => {
  let redirectPage: EOrderSuccessPages;
  const isRenewal = [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal].includes(action);

  if (['approved', 'success'].includes(status)) {
    redirectPage = isRenewal ? EOrderSuccessPages.renewConfirmed : EOrderSuccessPages.orderConfirmed;
  } else {
    redirectPage = isRenewal ? EOrderSuccessPages.renewWaitingPayment : EOrderSuccessPages.orderWaitingPayment;
  }

  return Router.replace({
    name: redirectPage,
    params,
    query: {
      ...Router.currentRoute.query,
    }
  });
};

export interface ITraceableErrorResponse {
  response?: any;
  code?: any;
  type?: string;
  exhibition?: EErrorExhibition;
}

export const treatCheckoutError = (error: any): any => {
  if (error && !error.response) {
    return addError(error);
  }

  if (error.response.status === 404) {
    addError(new TraceableError({
      code: error.response.status,
      type: EErrorType.loading,
      scope: EErrorScope.global,
      trigger: 'prepareCheckout',
      severity: EErrorSeverity.critical,
      exhibition: EErrorExhibition.none,
    }));

    return redirectAvailabilityErrorOr404();
  }

  const traceableError = getDefaultErrorByType(EErrorType.loading);

  return addError(new TraceableError({
    ...traceableError,
    code: error.response.status,
    type: EErrorType.loading,
    scope: EErrorScope.global,
    trigger: 'prepareCheckout',
    severity: EErrorSeverity.critical,
    exhibition: EErrorExhibition.modal,
  }));
};
